/**
 * Pure helpers + API types for the Vanilla Bot pane (/bots/vanilla). The pane
 * rides ONE relay poll (GET /api/vanilla/day — see relay routes/vanilla.ts);
 * these types mirror that response. No I/O here (unit-tested).
 */

export type VanillaGameStatus =
  | 'pending'
  | 'window_open'
  | 'quoting'
  | 'standing_down'
  | 'done'
  | 'halted';

/** Per-sport trading-knob overrides (mirror of the relay's VanillaSportOverride). */
export type VanillaSportOverride = Partial<
  Pick<
    VanillaDayConfig,
    | 'targetSumCents'
    | 'sizeContracts'
    | 'complianceMinSizePerSide'
    | 'perSideCapContracts'
    | 'queueRecycleAtUsd'
    | 'queueRecycleAtPct'
    | 'floorSumCents'
    | 'leanCents'
    | 'clipContracts'
    | 'rampIntervalMs'
    | 'polyGuardMarginCents'
    | 'entryMaxNoCents'
    | 'holdDriftCents'
    | 'bookStaleSec'
    | 'unpairedHaltContracts'
  >
>;

export interface VanillaDayConfig {
  quoteMode: 'mid' | 'sum97' | 'pingpong' | 'clipstack';
  sports?: string[]; // active sports whose games autofill (VANILLA_SPORTS); absent = ['MLB']
  sportConfigs?: Record<string, VanillaSportOverride>; // per-sport knob overrides
  targetSumCents: number;
  sizeContracts: number;
  complianceMinSizePerSide: number;
  perSideCapContracts: number;
  queueRecycleAtUsd: number;
  queueRecycleAtPct?: number; // pop out at <= pct% of entry queue-ahead (0 = off; absent = 50)
  // Fills-ahead gate on the pct pop-out (absent = ON): only drain consumed by
  // trades trips the trigger; pulls ahead of us are ignored. false = raw drain.
  queueRecycleFillsGate?: boolean;
  simpleMode?: boolean; // queue-only lifecycle: hold price, recycle on pop-out only (absent = off)
  // mm-defense posture panel (display-only): master switch + per-trigger-family
  // enables. Absent = enabled with every family on.
  defense?: { enabled?: boolean; families?: Record<string, boolean> };
  floorSumCents?: number; // elastic band floor (0 = off)
  leanCents?: number; // shift the slack split toward the liquid leg (0 = center)
  clipContracts?: number; // clip-ramp child size (0 = full size at once)
  rampIntervalMs?: number; // time per +1 clip
  polyGuardMarginCents?: number; // poly fair-value guard margin (0 = off)
  entryMaxNoCents?: number; // don't open a cycle when the dear leg's NO exceeds this (0 = off)
  holdDriftCents?: number; // pingpong drift band: pull a held quote when target falls >this below it (0 = default 3, -1 = off)
  bookStaleSec?: number; // per-ticker book silence bound in seconds (0 = default 600, -1 = off)
  unpairedHaltContracts?: number; // fill early-warning: auto-halt a sum97 game when |filledA − filledB| >= this (ABSENT = one clip / sizeContracts, explicit 0 = off — resolve via unpairedHaltLimit)
  dayBudgetUsd?: number; // fleet-wide day spend cap in whole USD (0 = off)
  institutionalMinContracts?: number; // org-only: a fill/order >= this = institutional ("banks"); smaller = recreational
  clipTiers?: ClipTierConfig[]; // Marty ladder tiers (SZ / STEP $ / GATE $ / contracts shield)
  clipMaxPerSide?: number; // slots per side per tier (default 5); ignored when clipTotalPerSideContracts > 0
  // TOTAL resting contracts per side per tier — the authoritative exposure
  // number. >0 = derived layout: ceil(total / clip size) clips (max 10), last
  // clip carries the remainder. 0/absent = legacy clipMaxPerSide shape.
  clipTotalPerSideContracts?: number;
  clipPolyGateMarginCents?: number; // Marty poly anchor+gate margin ¢ (default 0 = poly OFF, Kalshi-only; 1 = the doctrine when re-armed)
  clipDepthSelect?: boolean; // depth-aware split selection (absent = ON; false = legacy concede-richer kill switch)
  clipMaxOffTouchCents?: number; // per-side cap ¢ below the touch for the split search + widen fallback (absent = 2)
  clipRecheckMs?: number; // pre-join recheck delay ms — wait & re-read before joining a level (absent = 100, 0 = off)
  openTimeLocal: string;
  eodLocal: string;
  standDownLeadSec: number;
  intervalMs?: number;
}

export interface VanillaEventRecord {
  id: string;
  ts: number;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
  scope?: string; // 'day' | eventTicker (merged /events feed)
}

/** Live agent slice annotated onto a game by the conductor (g10edge Stats). */
export interface VanillaAgentView {
  id: string;
  paused?: boolean;
  posture?: string;
  quoteMode?: string;
  // In-memory runner action tape (newest last) — the Harry activity feed.
  actions?: Array<{ ts: number; level: 'info' | 'fill' | 'warn'; msg: string }> | null;
  sitOutReason?: string | null; // why it's standing down ('' / absent = quoting)
  resting?: Array<{ side: 'bid' | 'ask'; priceCents: number; sizeContracts: number }> | null;
  noTargetA?: number;
  noTargetB?: number;
  filledNoA?: number;
  filledNoB?: number;
  queueEntryA?: number;
  queueNowA?: number;
  queueEntryB?: number;
  queueNowB?: number;
  fillsAheadA?: number;
  pullsAheadA?: number;
  fillsAheadB?: number;
  pullsAheadB?: number;
  // Pct pop-outs the fills-ahead gate held back (raw drain hit the threshold
  // but trades alone didn't) — both legs, runner lifetime.
  recyclesSuppressed?: number;
  // Recreational-vs-institutional fill split (game-level; deferred — needs a
  // per-trade feed, so these stay 0 and the rest-quality inst axis reads "—").
  instFillContracts?: number;
  recFillContracts?: number;
  concessionA?: number;
  concessionB?: number;
  eligibleSeconds?: number;
  compliantFullSeconds?: number;
  compliantUptimePct?: number;
  errors?: number;
  // Queue-position moves this session (in-memory; resets on relay restart):
  // recycles = shield/pop-out jumps, requotes = both-leg cancel-replace on drift.
  recycles?: number;
  // Clip-stack shield trips (clipstack only). Which defense is doing the work —
  // all three zero after a slate means the shield is armed but untested.
  gateAheadTrips?: number; // contracts-ahead warn floor
  criticalTrips?: number; // level nearly empty → conceded a cent
  drainTrips?: number; // projected to cross the warn floor within the horizon
  gateThinHolds?: number; // gate tripped but re-queue would gain nothing — held
  sumFallbacks?: number; // anchored pair summed under target — book join instead
  polyDistrusts?: number; // poly flagged untrusted (divergence vs fresh book)
  depthPicks?: number; // depth score moved the split off concede-richer (episodes)
  widenFallbacks?: number; // no safe compliant split — widened below target onto walls
  depthFallbacks?: number; // depth selection fell back to concede-richer (episodes)
  recheckAborts?: number; // pre-join rechecks that aborted (clearing / moved / stale)
  requotes?: number;
  // Harry L1 ping-pong cycle state (null unless quoteMode === 'pingpong').
  pingpong?: {
    activeLeg: 'a' | 'b';
    pairsCompleted: number;
    cycleFilled: number;
    cycleSize: number;
    legOneNo: number; // completed leg 1's max placed NO (-1 = phase 1)
  } | null;
  // mm-defense per-game chip (Marty; null/absent = not evaluated this tick).
  defense?: VanillaDefenseGameState | null;
  // Marty clip-stack ladder mirror (null unless quoteMode === 'clipstack').
  ladder?: LadderView | null;
}

// ── Marty clip-stack ladder (mirror of relay clipTypes.ts wire shapes) ──
export type ClipIntent = 'white' | 'orange';
/** Mirror of the relay's ClipTierConfig. The contracts-shield fields are
 * optional on the wire because clipTiersOf fills the defaults server-side. */
export interface ClipTierConfig {
  sizeContracts: number;
  stepUsd: number;
  gateUsd: number;
  gateAheadContracts?: number;
  criticalAheadContracts?: number;
  criticalConcessionCents?: number;
}
/** Mirrors of the relay's shield defaults (vanillaMm/config.ts). The drawer
 * must DISPLAY these when a tier field is unset — the server arms them on
 * absent fields, so rendering `?? 0` would show a shield that reads OFF while
 * it is actually running at 50k/10k/1¢. Explicit 0 really is off. */
export const GateAheadContractsDefault = 50_000;
export const CriticalAheadContractsDefault = 10_000;
export const CriticalConcessionCentsDefault = 2;
export const MaxOffTouchCentsDefault = 2;
export const ClipRecheckMsDefault = 100;
export interface ClipView {
  clipId: string;
  side: 'a' | 'b';
  tier: number;
  slot: number;
  intent: ClipIntent;
  state: 'idle' | 'resting' | 'recycling' | 'filled' | 'expired' | 'killed';
  /** Queue-ahead at entry (this order's baseline) — the card bar's 100%. */
  entryAheadContracts?: number;
  yesPriceCents: number;
  sizeContracts: number;
  aheadContracts: number;
  aheadUsd: number | null;
  placedAtMs: number;
  stale: boolean;
}
export interface LadderTierView {
  tier: number;
  yesA: number;
  yesB: number;
  /** True = prices came from the poly anchor; false/absent = Kalshi book join
   * (poly unresolved, untrusted, or its pair summed under target). */
  anchored?: boolean;
  /** Book-path selection provenance: 'depth' = joined the walls, 'widen' =
   * gave up sum for a wall (spread_wide), 'held' = stability, 'join'/'fallback'
   * = concede-richer behavior. Absent when anchored or legacy selector. */
  splitSource?: 'join' | 'held' | 'depth' | 'widen' | 'fallback';
  cfg: ClipTierConfig;
}
export interface LadderSideView {
  restContracts: number;
  filledContracts: number;
  leadAheadUsd: number | null;
  drainUsdPerMin: number | null;
  /** Worst concession held by any clip on this side, cents. Non-zero = we have
   * traded spread (and therefore uptime) for shelter from a collapsing level. */
  concessionCents?: number;
  /** Queue-ahead distribution across the side's resting clips, in contracts —
   * the evidence for where the shield floors actually belong on these books. */
  minAheadContracts?: number | null;
  medianAheadContracts?: number | null;
  paused: boolean;
}
export interface LadderView {
  tiers: LadderTierView[];
  clips: ClipView[];
  sideA: LadderSideView;
  sideB: LadderSideView;
  /** Poly peg diverged from a fresh Kalshi book for 2m+ — anchor + veto off,
   * quoting off the book until it converges. */
  polyUntrusted?: boolean;
  quoteUntilMs: number;
  asOfMs: number;
}

// ── mm-defense wire shapes (mirror of the relay's VanillaDefenseWire) ──

export type VanillaDefensePosture = 'green' | 'yellow' | 'orange' | 'red';

export interface VanillaDefenseGameState {
  posture: VanillaDefensePosture;
  triggerCount: number;
}

/** Fleet-level defense posture annotated onto GET /api/vanilla/day by the
 * relay's mm-defense adapter (display-only: recommended actions are shown as
 * would-do, never enacted client-side). */
export interface VanillaDefenseView {
  posture: VanillaDefensePosture;
  summary: string;
  triggerCount: { tier1: number; tier2: number; tier3: number };
  activeTriggers: Array<{
    type: string;
    severity: 'tier1' | 'tier2' | 'tier3';
    message: string;
    tickers: string[];
    value: number;
    threshold: number;
    evaluatedAtMs: number;
  }>;
  recommendedActions: Array<{
    type: string;
    priority: number;
    description: string;
    causedBy: string[];
    tickers: string[];
  }>;
  /** Per-game states keyed by agentId (join on VanillaGameView.agentId). */
  byGame: Record<string, VanillaDefenseGameState>;
  evaluatedAtMs: number;
}

export interface VanillaGameView {
  id: string;
  eventTicker: string;
  tickerAway: string;
  tickerHome: string;
  awayCode: string | null;
  homeCode: string | null;
  status: VanillaGameStatus;
  halted: boolean;
  haltReason: string | null;
  firstPitchMs: number | null;
  anchorSource: string | null;
  windowOpenMs: number | null;
  windowCloseMs: number | null;
  agentId: string;
  lastError: string | null;
  events: VanillaEventRecord[];
  // Per-game config override (Harry: quoteMode 'pingpong' on one game).
  configOverride?: Partial<VanillaDayConfig> | null;
  agent: VanillaAgentView | null;
  compliance: { eligibleMs: number; compliantMs: number; fills: number; uptimePct: number | null } | null;
}

export interface VanillaConductorHealth {
  running: boolean;
  laDate: string;
  edgeReachable: boolean;
  edgeDryRun: boolean | null;
  hydraFresh: boolean;
  lastError: string | null;
  gameCounts: Partial<Record<VanillaGameStatus, number>>;
  /** Discovery board stats (optional — older relays omit it). unpairedEvents
   * lists games hydra served but discovery couldn't pair into away/home legs;
   * without this they'd be invisibly missing from the picker. */
  board?: {
    games: number;
    ageMs: number | null;
    unpairedEvents?: Array<{ eventTicker: string; reason: string }>;
  };
}

/** One open bot position row (position_states slice; DB-backed, so it
 * survives Kill All and dead runners). avgCostCents = costBasis / netCount. */
export interface VanillaBotPosition {
  ticker: string;
  side: string; // 'yes' | 'no'
  netCount: number;
  avgCostCents: number;
}

/** One row of the relay's Marty board (mirror of the relay's BoardGame + the
 * liveness stamp vanillaBoard() adds). Prices are integer cents; null means the
 * venue has no usable book for that leg. */
export interface VanillaBoardGame {
  eventTicker: string;
  tickerAway: string;
  tickerHome: string;
  awayCode: string;
  homeCode: string;
  /** Display names from kalshi; '' when absent (fall back to the codes). */
  awayName: string;
  homeName: string;
  /** Per-leg observed Kalshi shard identity; absent remains unknown. */
  exchangeIndexAway?: number;
  exchangeIndexHome?: number;
  sport: string;
  gameKeyV2?: string;
  /** Ticker-embedded ET start (epoch ms); 0 when the ticker carries no time. */
  tickerEtMs: number;
  kalshiAwayCents: number | null;
  kalshiHomeCents: number | null;
  polyAwayCents: number | null;
  polyHomeCents: number | null;
  /** Hydra statuses of the two kalshi legs — input to gameState derivation. */
  statuses: string[];
  /** True when the prices came from the live per-leg book (slated games) rather
   * than the ~60s discovery snapshot (candidates). */
  live: boolean;
  atMs: number;
}

export interface VanillaDayResponse {
  laDate: string;
  day: { id: string; enabled: boolean; config: VanillaDayConfig } | null;
  defaultConfig?: VanillaDayConfig;
  games: VanillaGameView[];
  /** Discovery board: every addable game today with venue prices, built relay-
   * side off the conductor's hydra fetch. Marty renders its picker and price
   * columns from this instead of deriving them from the browser's markets
   * firehose. Absent on older relays / the no-day-row shape. */
  board?: VanillaBoardGame[];
  // The bot's relay identity + open inventory (absent on the no-day-row shape).
  bot?: { username: string; positions: VanillaBotPosition[] };
  budget?: { spentCents: number; budgetCents: number };
  // mm-defense fleet posture (null = disabled or nothing to evaluate).
  defense?: VanillaDefenseView | null;
  dryRun: boolean | null;
  // Dev/demo tooling reachable (POST /api/vanilla/dev/drill) — shows the
  // Defense 3 drill button. Off-prod always; prod only with VANILLA_DRILL=1.
  drillAvailable?: boolean;
  health: VanillaConductorHealth;
  serverTs?: number;
}

// ---------------------------------------------------------------------------

/** Sports the relay's auto-discovery/picker supports (mirror of the relay's
 * VANILLA_GAME_SERIES sports — two-outcome game series only; no Soccer, whose
 * 3-way draw leg breaks the sum97 pair economics). */
export const VANILLA_SPORTS = ['MLB', 'NBA', 'WNBA', 'NFL', 'NHL', 'NCAA', 'CFB', 'Esports', 'Cricket'] as const;
export type VanillaSport = (typeof VANILLA_SPORTS)[number];

/** Pane sport → dashboard marketStore Sport id (getGames key). NCAA collapses
 * to the dashboard's college-basketball tab 'cbb'. */
export const VANILLA_SPORT_TO_DASH: Record<VanillaSport, string> = {
  MLB: 'mlb',
  NBA: 'nba',
  WNBA: 'wnba',
  NFL: 'nfl',
  NHL: 'nhl',
  NCAA: 'cbb',
  CFB: 'cfb',
  Esports: 'esports',
  Cricket: 'cricket',
};

/** One sport's effective knobs = day defaults with its sportConfigs override
 * layered on. Mirror of the relay's day→sport layer (game-override layer is
 * server-only). */
export function effectiveSportConfig(day: VanillaDayConfig, sport: VanillaSport): VanillaDayConfig {
  return { ...day, ...(day.sportConfigs?.[sport] ?? {}) };
}

/** Series prefix → sport, mirror of the relay allowlist (pill display + the
 * picker's already-slated dimming; the relay re-validates on POST). */
const GAME_SERIES_SPORT: ReadonlyArray<[string, VanillaSport]> = [
  ['KXMLBGAME-', 'MLB'],
  ['KXNBAGAME-', 'NBA'],
  ['KXWNBAGAME-', 'WNBA'],
  ['KXNFLGAME-', 'NFL'],
  ['KXNHLGAME-', 'NHL'],
  ['KXNCAAMBGAME-', 'NCAA'],
  ['KXNCAAWBGAME-', 'NCAA'],
  ['KXNCAAGAME-', 'NCAA'],
  ['KXNCAAFGAME-', 'CFB'],
  ['KXCS2GAME-', 'Esports'],
  ['KXDOTA2GAME-', 'Esports'],
  ['KXVALORANTGAME-', 'Esports'],
  ['KXLOLGAME-', 'Esports'],
  ['KXWT20MATCH-', 'Cricket'],
];

/** Sport of a game event/market ticker, null when not a pair-safe series. */
export function sportOfEventTicker(ticker: string): VanillaSport | null {
  for (const [prefix, sport] of GAME_SERIES_SPORT) {
    if (ticker.startsWith(prefix)) return sport;
  }
  return null;
}

/** Event ticker of a game market ticker (strip the outcome suffix; some
 * series separate the outcome with '--', so trailing dashes are trimmed).
 * Mirror of the relay's eventTickerOf. */
export function eventTickerOfMarket(marketTicker: string): string {
  const i = marketTicker.lastIndexOf('-');
  return i > 0 ? marketTicker.slice(0, i).replace(/-+$/, '') : marketTicker;
}

export const STATUS_CHIP: Record<VanillaGameStatus, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-muted text-muted-foreground' },
  window_open: { label: 'Opening', cls: 'bg-blue-500/15 text-blue-500' },
  quoting: { label: 'Quoting', cls: 'bg-emerald-500/15 text-emerald-500' },
  standing_down: { label: 'Standing down', cls: 'bg-amber-500/15 text-amber-600' },
  done: { label: 'Done', cls: 'bg-muted text-muted-foreground' },
  halted: { label: 'Halted', cls: 'bg-destructive/15 text-destructive' },
};

/** mm:ss (or h:mm:ss) until a deadline; '' when past/unknown. */
export function formatCountdown(deadlineMs: number | null | undefined, nowMs: number): string {
  if (!deadlineMs || deadlineMs <= nowMs) return '';
  const total = Math.floor((deadlineMs - nowMs) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** LA wall-clock 'h:mm AM' of an epoch instant (first-pitch display). */
export function formatLaTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: '2-digit',
  }).format(ms);
}

/**
 * Uptime badge tone: green >= 98, amber >= 95, red below (the rebate bars).
 * Dry-run uptime is a simulation — tone stays neutral so red never lies.
 */
export function uptimeTone(pct: number | null | undefined, dryRun: boolean): 'green' | 'amber' | 'red' | 'neutral' {
  if (pct == null) return 'neutral';
  if (dryRun) return 'neutral';
  if (pct >= 98) return 'green';
  if (pct >= 95) return 'amber';
  return 'red';
}

/** Fill early-warning limit — FE mirror of the relay's `unpairedHaltLimit`
 * (`relay/src/services/vanillaSlate.ts`). Absent means armed at one clip
 * (`sizeContracts`); an explicit 0 means off. Keep the two in lockstep: a UI
 * that renders "off" while the rail is armed is worse than no display. */
export function unpairedHaltLimit(
  cfg: { unpairedHaltContracts?: number; sizeContracts?: number } | null | undefined,
): number {
  if (!cfg) return 0;
  if (cfg.unpairedHaltContracts === undefined || cfg.unpairedHaltContracts === null) {
    return (cfg.sizeContracts ?? 0) > 0 ? (cfg.sizeContracts as number) : 0;
  }
  return cfg.unpairedHaltContracts;
}

/** Exposure meter fill fraction, clamped [0,1]. */
export function exposureFraction(filled: number, cap: number): number {
  if (cap <= 0) return 0;
  return Math.min(1, Math.max(0, filled / cap));
}

/** Matchup label: 'MIA @ NYY' with ticker fallback. */
export function matchupLabel(g: Pick<VanillaGameView, 'awayCode' | 'homeCode' | 'eventTicker'>): string {
  if (g.awayCode && g.homeCode) return `${g.awayCode} @ ${g.homeCode}`;
  return g.eventTicker;
}

/** Day-level rollup for the SummaryStrip. */
export function daySummary(games: VanillaGameView[]): {
  total: number;
  quoting: number;
  halted: number;
  done: number;
  fills: number;
  uptimePct: number | null;
} {
  let eligible = 0;
  let compliant = 0;
  let fills = 0;
  let quoting = 0;
  let halted = 0;
  let done = 0;
  for (const g of games) {
    if (g.status === 'quoting') quoting++;
    if (g.status === 'halted') halted++;
    if (g.status === 'done') done++;
    if (g.compliance) {
      eligible += g.compliance.eligibleMs;
      compliant += g.compliance.compliantMs;
      fills += g.compliance.fills;
    }
  }
  return {
    total: games.length,
    quoting,
    halted,
    done,
    fills,
    uptimePct: eligible > 0 ? (100 * compliant) / eligible : null,
  };
}
